import { useCallback, useEffect, useMemo, useState } from "react";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { GET_FEATURES } from "@src/domain/graphql";
import type {
  FeatureLookupItem,
  FeaturesResponse,
} from "@src/domain/dto/features";

export interface FeaturesLookupSlice {
  featuresLookup: FeatureLookupItem[] | null;
  featuresLookupCount: number;
  featuresLookupLoading: boolean;
  featuresLookupError: string | null;
  refetchFeaturesLookup: () => Promise<void>;
}

export function useFeaturesLookup(): FeaturesLookupSlice {
  const [featuresLookup, setFeaturesLookup] = useState<FeatureLookupItem[] | null>(
    null,
  );
  const [featuresLookupCount, setFeaturesLookupCount] = useState(0);
  const [featuresLookupLoading, setFeaturesLookupLoading] = useState(true);
  const [featuresLookupError, setFeaturesLookupError] = useState<string | null>(
    null,
  );

  const refetchFeaturesLookup = useCallback(async () => {
    setFeaturesLookupLoading(true);
    setFeaturesLookupError(null);
    try {
      const client = GraphQlAdapter.getInstance().getClient();
      const result = await client.query<FeaturesResponse>({
        query: GET_FEATURES,
      });
      const data = result.data?.featuresLookup;
      if (data) {
        setFeaturesLookup(data.items ?? null);
        setFeaturesLookupCount(data.count ?? 0);
      } else {
        setFeaturesLookup(null);
        setFeaturesLookupCount(0);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load features";
      setFeaturesLookupError(message);
      setFeaturesLookup(null);
      setFeaturesLookupCount(0);
    } finally {
      setFeaturesLookupLoading(false);
    }
  }, []);

  useEffect(() => {
    refetchFeaturesLookup();
  }, [refetchFeaturesLookup]);

  return useMemo(
    () => ({
      featuresLookup,
      featuresLookupCount,
      featuresLookupLoading,
      featuresLookupError,
      refetchFeaturesLookup,
    }),
    [
      featuresLookup,
      featuresLookupCount,
      featuresLookupLoading,
      featuresLookupError,
      refetchFeaturesLookup,
    ],
  );
}
